import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Button } from 'shared-components';
import { get } from 'shared-components/src/utils/http';
import { ProgramConfigContext } from '../../contexts/ProgramConfigContext';

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  color: ${({ status, theme }) =>
    status === 'successful'
      ? (theme.colors.primary || '#2e7d32')
      : '#b07d00'};
  border: 1px solid ${({ status, theme }) =>
    status === 'successful'
      ? (theme.colors.primary || '#2e7d32')
      : '#b07d00'};

  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }
`;

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" />
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
    <path d="M21.801 10A10 10 0 1 1 17 3.335" />
    <path d="m9 11 3 3L22 4" />
  </svg>
);

const CraDdaStatusBadge = ({ accountId, onEnroll }) => {
  const intl = useIntl();
  const programConfig = useContext(ProgramConfigContext);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const isEnabled = programConfig && programConfig.cra_dda_enabled;

  useEffect(() => {
    if (!isEnabled || !accountId) {
      setLoading(false);
      return;
    }

    const fetchStatus = async () => {
      try {
        const res = await get(
          `/api/v1/cardholder_portal/cardholder/accounts/${accountId}/cra-dda-participant`
        );
        setStatus(res.registration_status);
      } catch (e) {
        // No record — don't show badge
        setStatus(null);
      }
      setLoading(false);
    };
    fetchStatus();
  }, [accountId, isEnabled]);

  if (!isEnabled || loading) return null;

  const isPending = status === 'pending' || status === 'submitted';
  const isEnrolled = status === 'successful';

  // Enrolled: show completed button
  if (isEnrolled) {
    return (
      <Button type="primary" size="medium" disabled style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#f1f5f9', color: '#333', border: '1px solid #f1f5f9', opacity: 1 }}>
        <CheckIcon /> {intl.messages['cra-dda-success-done']}
      </Button>
    );
  }

  // Pending: show pending badge
  if (isPending) {
    return (
      <Badge status="pending">
        <ClockIcon />
        {intl.messages['cra-dda-status-pending']}
      </Badge>
    );
  }

  // No record: show enroll button
  if (onEnroll) {
    return (
      <Button type="primary" size="medium" onClick={onEnroll}>
        {intl.messages['cra-dda-enroll-button']}
      </Button>
    );
  }

  return null;
};

CraDdaStatusBadge.propTypes = {
  accountId: PropTypes.number.isRequired,
  onEnroll: PropTypes.func,
};

CraDdaStatusBadge.defaultProps = {
  onEnroll: null,
};

export default CraDdaStatusBadge;
